import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Divider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Collapse,
  Badge
} from "@material-ui/core";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  SwapHoriz as SwapHorizIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Message as MessageIcon,
  ViewColumn as ViewColumnIcon
} from "@material-ui/icons";
import { format } from "date-fns";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useTicketCard from "../../hooks/useTicketCard";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: "100%"
  },
  title: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2)
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  formControl: {
    margin: theme.spacing(1, 0)
  },
  selectEmpty: {
    marginTop: theme.spacing(1)
  },
  button: {
    marginTop: theme.spacing(1)
  },
  buttonCreate: {
    marginTop: theme.spacing(2)
  },
  priorityLow: {
    color: theme.palette.success.main
  },
  priorityMedium: {
    color: theme.palette.warning.main
  },
  priorityHigh: {
    color: theme.palette.error.main
  },
  datePickerField: {
    "& .MuiInputBase-input": {
      padding: "10px 14px"
    }
  },
  progressContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3)
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: "0.8rem"
  },
  messagesSection: {
    marginTop: theme.spacing(2)
  },
  messageItem: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default
  },
  messageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  messageContent: {
    whiteSpace: "pre-line",
    wordBreak: "break-word"
  },
  messagesTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer"
  },
  messageCount: {
    display: "flex",
    alignItems: "center"
  },
}));

const TicketKanbanPanel = ({ ticketId, ticketStatus, queueId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { 
    loading, 
    card, 
    setCard,  // Adicionando o setter do estado card
    stages, 
    createCard, 
    updateCard, 
    moveCard, 
    fetchStages 
  } = useTicketCard(ticketId);

  const [editMode, setEditMode] = useState(false);
  const [changeQueueMode, setChangeQueueMode] = useState(false);
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    stageId: "",
    priority: "medium",
    tags: "",
    dueDate: null
  });
  
  // Estado local para armazenar os estágios da fila atual
  const [queueStages, setQueueStages] = useState([]);
  // Estado local para armazenar os estágios da fila selecionada
  const [selectedQueueStages, setSelectedQueueStages] = useState([]);
  
  // Estado para controlar o modo de criação de cartão
  const [createMode, setCreateMode] = useState(false);
  const [newCardData, setNewCardData] = useState({
    title: "",
    description: "",
    priority: "medium",
    tags: "",
    queueId: ""
  });

  // Estados para gerenciar as mensagens vinculadas ao cartão
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [cardMessages, setCardMessages] = useState([]);
  const [messagesExpanded, setMessagesExpanded] = useState(false);

  // Determinar o idioma atual para o DatePicker
  const locale = i18n.language === "pt" ? ptBR : enUS;

  useEffect(() => {
    if (card) {
      setFormData({
        title: card.title || "",
        description: card.description || "",
        stageId: card.stageId,
        priority: card.priority || "medium",
        tags: card.tags || "",
        dueDate: card.dueDate ? new Date(card.dueDate) : null
      });
      
      if (card.stage && card.stage.queueId) {
        setSelectedQueue(card.stage.queueId.toString());
      }
    }
  }, [card]);

  // Buscar estágios da fila do ticket, não da fila do cartão
  useEffect(() => {
    if (queueId) {
      const fetchQueueStages = async () => {
        try {
          const { data } = await fetchStages(queueId);
          setQueueStages(data || []);
        } catch (err) {
          console.error("Erro ao buscar estágios da fila:", err);
          setQueueStages([]);
        }
      };
      
      fetchQueueStages();
    }
  }, [queueId, fetchStages]);

  // Carregar todas as filas disponíveis
  useEffect(() => {
    const fetchQueues = async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        console.error("Erro ao carregar filas:", err);
        toastError(err);
      }
    };

    fetchQueues();
  }, []);

  // Carregar estágios da fila selecionada quando mudar de fila
  useEffect(() => {
    if (selectedQueue && changeQueueMode) {
      const fetchSelectedQueueStages = async () => {
        try {
          const { data } = await api.get(`/kanban/stages/queue/${selectedQueue}`);
          setSelectedQueueStages(data || []);
        } catch (err) {
          console.error("Erro ao buscar estágios da fila selecionada:", err);
          setSelectedQueueStages([]);
        }
      };
      
      fetchSelectedQueueStages();
    }
  }, [selectedQueue, changeQueueMode]);

  // Carregar as mensagens vinculadas ao cartão
  useEffect(() => {
    const fetchCardMessages = async () => {
      if (!card || !card.id) return;
      
      try {
        setLoadingMessages(true);
        const { data } = await api.get(`/kanban/cards/${card.id}/messages`);
        setCardMessages(data);
      } catch (err) {
        console.error("Erro ao carregar mensagens do cartão:", err);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchCardMessages();
  }, [card]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQueueChange = (e) => {
    setSelectedQueue(e.target.value);
    // Resetar o estágio quando mudar de fila
    setFormData(prev => ({ ...prev, stageId: "" }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, dueDate: date }));
  };

  const handleCreateCardClick = async () => {
    try {
      // Sempre buscar os dados atualizados do ticket para garantir que estamos usando a fila correta
      const { data: ticketData } = await api.get(`/tickets/${ticketId}`);
      const ticketQueueId = ticketData.queueId;
      
      if (!ticketQueueId) {
        toast.error(i18n.t("ticketCard.errors.noQueue") || "Este ticket não está associado a nenhuma fila");
        return;
      }
      
      // Prepopular o título com o número do ticket e nome do contato e definir a fila atual
      setNewCardData({
        title: `#${ticketId} - ${ticketData.contact?.name || ""}`,
        description: "",
        priority: "medium",
        tags: "",
        queueId: ticketQueueId.toString()
      });
      
      // Ativar o modo de criação
      setCreateMode(true);
    } catch (err) {
      console.log('Erro', err);
      toastError(err);
    }
  };

  const handleCreateCard = async () => {
    try {
      console.log("[DEBUG] Criando novo cartão com dados:", newCardData);
      
      // Sempre buscar os dados atualizados do ticket para garantir que estamos usando a fila correta
      const { data: ticketData } = await api.get(`/tickets/${ticketId}`);
      const ticketQueueId = ticketData.queueId;
      
      if (!ticketQueueId) {
        toast.error(i18n.t("ticketCard.errors.noQueue") || "Este ticket não está associado a nenhuma fila");
        return;
      }
      
      // Verificar se a descrição está preenchida
      if (!newCardData.description) {
        toast.error("A descrição é obrigatória");
        return;
      }

      try {
        // Enviar todos os dados de uma vez na criação do cartão
        const { data } = await api.post("/kanban/cards", {
          title: newCardData.title,
          description: newCardData.description, // Garantir que a descrição está sendo enviada
          ticketId,
          queueId: ticketQueueId,
          userId: user?.id,
          priority: newCardData.priority,
          tags: newCardData.tags
        });
        
        console.log("[DEBUG] Cartão criado com sucesso:", data);
        setCard(data);
        
        toast.success(i18n.t("ticketCard.panel.cardCreatedSuccess") || "Cartão criado com sucesso!");
      } catch (error) {
        console.error("[DEBUG] Erro ao criar cartão:", error);
        throw error;
      }
      
      // Limpar o formulário e voltar ao modo normal
      setCreateMode(false);
      setNewCardData({
        title: "",
        description: "",
        priority: "medium",
        tags: "",
        queueId: ""
      });
      
    } catch (err) {
      console.log('Erro', err);
      toastError(err);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Certificar-se de que o estágio selecionado pertence à fila atual
      if (formData.stageId) {
        const stageExists = queueStages.some(stage => stage.id === formData.stageId);
        if (!stageExists) {
          // Se o estágio não pertencer à fila atual, usar o estágio inicial da fila atual
          const initialStage = queueStages.find(stage => stage.isInitial);
          if (initialStage) {
            formData.stageId = initialStage.id;
          }
        }
      }
      
      await updateCard(formData);
      setEditMode(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleMoveCard = async (stageId) => {
    try {
      // Verificar se o estágio de destino pertence à fila atual
      const stageExists = queueStages.some(stage => stage.id === stageId);
      if (!stageExists) {
        toastError(new Error("Estágio de destino não pertence à fila atual"));
        return;
      }
      
      await moveCard(stageId);
    } catch (err) {
      toastError(err);
    }
  };
  
  // Função para mudar o cartão de fila
  const handleChangeQueue = async () => {
    try {
      if (!selectedQueue || !formData.stageId) {
        toastError(new Error(i18n.t("ticketCard.panel.selectQueueAndStage")));
        return;
      }
      
      // Primeiro atualizar o ticket para a nova fila
      await api.put(`/tickets/${ticketId}`, {
        queueId: selectedQueue
      });
      
      // Atualizar o cartão com os novos dados
      await api.put(`/kanban/cards/${card.id}`, {
        queueId: selectedQueue,
        ...formData
      });
      
      // Mover o cartão para o novo estágio
      await api.put(`/kanban/cards/${card.id}/move`, { 
        stageId: formData.stageId 
      });
      
      // Mostrar mensagem de sucesso
      toast.success(i18n.t("ticketCard.panel.queueChangeSuccess") || "Ticket movido para a nova fila com sucesso!");
      
      // Atualizar o estado local
      setChangeQueueMode(false);
      
      // Recarregar a página para mostrar as mudanças
      window.location.reload();
    } catch (err) {
      console.error("Erro ao mudar de fila:", err);
      toastError(err);
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "low":
        return i18n.t("ticketCard.panel.priorityOptions.low");
      case "high":
        return i18n.t("ticketCard.panel.priorityOptions.high");
      case "medium":
      default:
        return i18n.t("ticketCard.panel.priorityOptions.medium");
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "low":
        return classes.priorityLow;
      case "high":
        return classes.priorityHigh;
      case "medium":
      default:
        return classes.priorityMedium;
    }
  };

  const getNextStage = () => {
    if (!card || !queueStages.length) return null;
    
    // Ordenar estágios por posição
    const sortedStages = [...queueStages].sort((a, b) => a.position - b.position);
    
    // Encontrar o índice do estágio atual
    const currentStageIndex = sortedStages.findIndex(s => s.id === card.stageId);
    
    // Se é o último estágio ou não foi encontrado, retornar null
    if (currentStageIndex === -1 || currentStageIndex === sortedStages.length - 1) {
      return null;
    }
    
    // Retornar o próximo estágio
    return sortedStages[currentStageIndex + 1];
  };

  // Se o ticket estiver fechado, não mostrar o painel
  if (ticketStatus === "closed") {
    return null;
  }

  if (loading) {
    return (
      <Paper className={classes.root} variant="outlined">
        <div className={classes.progressContainer}>
          <CircularProgress size={30} />
        </div>
      </Paper>
    );
  }

  if (!card) {
    // Se não há cartão, mostrar formulário de criação ou botão para criar
    return (
      <Paper className={classes.root} variant="outlined">
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6" className={classes.title}>
              {createMode 
                ? i18n.t("ticketCard.panel.createCard") 
                : i18n.t("ticketCard.panel.title")
              }
            </Typography>
          </Grid>
          {createMode && (
            <Grid item>
              <Tooltip title={i18n.t("ticketCard.panel.cancel")}>
                <IconButton
                  size="small"
                  onClick={() => setCreateMode(false)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
        <Divider className={classes.divider} />
        
        {createMode ? (
          // Formulário de criação de cartão
          <>
            <TextField
              label={i18n.t("ticketCard.panel.title")}
              name="title"
              value={newCardData.title}
              onChange={(e) => setNewCardData({ ...newCardData, title: e.target.value })}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              className={classes.formControl}
            />
            
            <TextField
              label={i18n.t("ticketCard.panel.description")}
              name="description"
              value={newCardData.description}
              onChange={(e) => setNewCardData({ ...newCardData, description: e.target.value })}
              fullWidth
              multiline
              minRows={3}
              margin="dense"
              variant="outlined"
              className={classes.formControl}
            />
            
            <FormControl 
              variant="outlined" 
              fullWidth 
              className={classes.formControl}
              size="small"
            >
              <InputLabel>{i18n.t("ticketCard.panel.priority")}</InputLabel>
              <Select
                name="priority"
                value={newCardData.priority}
                onChange={(e) => setNewCardData({ ...newCardData, priority: e.target.value })}
                label={i18n.t("ticketCard.panel.priority")}
              >
                <MenuItem value="low">
                  {i18n.t("ticketCard.panel.priorityOptions.low")}
                </MenuItem>
                <MenuItem value="medium">
                  {i18n.t("ticketCard.panel.priorityOptions.medium")}
                </MenuItem>
                <MenuItem value="high">
                  {i18n.t("ticketCard.panel.priorityOptions.high")}
                </MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label={i18n.t("ticketCard.panel.tags")}
              name="tags"
              value={newCardData.tags}
              onChange={(e) => setNewCardData({ ...newCardData, tags: e.target.value })}
              fullWidth
              margin="dense"
              variant="outlined"
              size="small"
              className={classes.formControl}
              placeholder="tag1, tag2, tag3"
            />
            
            <FormControl
              variant="outlined"
              fullWidth
              className={classes.formControl}
              size="small"
              disabled
            >
              <InputLabel>{i18n.t("kanban.fields.queue")}</InputLabel>
              <Select
                value={newCardData.queueId}
                label={i18n.t("kanban.fields.queue")}
                displayEmpty
              >
                {queues
                  .filter(q => q.id.toString() === newCardData.queueId)
                  .map(queue => (
                    <MenuItem key={queue.id} value={queue.id.toString()}>
                      {queue.name}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleCreateCard}
              disabled={!newCardData.title || !newCardData.description}
              className={classes.button}
              size="small"
            >
              {i18n.t("ticketCard.panel.createCard")}
            </Button>
          </>
        ) : (
          // Botão para iniciar a criação de cartão
          <>
            <Typography variant="body2" gutterBottom>
              {i18n.t("ticketCard.panel.emptyCard")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<AddIcon />}
              className={classes.buttonCreate}
              onClick={handleCreateCardClick}
              disabled={ticketStatus !== "open"}
            >
              {i18n.t("ticketCard.panel.createCard")}
            </Button>
          </>
        )}
      </Paper>
    );
  }

  const nextStage = getNextStage();

  // Se o cartão pertence a uma fila diferente da fila atual do ticket, exibir aviso
  const isCardFromDifferentQueue = card.queueId && queueId ? card.queueId.toString() !== queueId.toString() : false;
  
  // Encontrar o nome da fila atual do ticket
  const currentQueue = queues.find(q => q.id.toString() === queueId?.toString())?.name || "";
  
  // Encontrar o nome da fila do cartão
  const cardQueue = queues.find(q => q.id.toString() === card.queueId?.toString())?.name || "";

  if (changeQueueMode) {
    return (
      <Paper className={classes.root} variant="outlined">
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6" className={classes.title}>
              {i18n.t("ticketCard.panel.moveToQueue")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={i18n.t("ticketCard.panel.cancel")}>
              <IconButton
                size="small"
                onClick={() => setChangeQueueMode(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        
        <FormControl 
          variant="outlined" 
          fullWidth 
          className={classes.formControl}
          size="small"
        >
          <InputLabel>{i18n.t("ticketCard.panel.queue")}</InputLabel>
          <Select
            value={selectedQueue || ""}
            onChange={handleQueueChange}
            label={i18n.t("ticketCard.panel.queue")}
          >
            {queues.map(queue => (
              <MenuItem key={queue.id} value={queue.id.toString()}>
                {queue.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {selectedQueue && (
          <FormControl 
            variant="outlined" 
            fullWidth 
            className={classes.formControl}
            size="small"
          >
            <InputLabel>{i18n.t("ticketCard.panel.stage")}</InputLabel>
            <Select
              name="stageId"
              value={formData.stageId || ""}
              onChange={handleChange}
              label={i18n.t("ticketCard.panel.stage")}
            >
              {selectedQueueStages.map(stage => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleChangeQueue}
          className={classes.button}
          disabled={!selectedQueue || !formData.stageId}
        >
          {i18n.t("ticketCard.panel.moveToQueueButton")}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root} variant="outlined">
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h6" className={classes.title}>
            {i18n.t("ticketCard.panel.title")}
          </Typography>
        </Grid>
        <Grid item>
          {!editMode ? (
            <>
              <Tooltip title={i18n.t("ticketCard.panel.changeQueue") || "Mudar de Fila"}>
                <IconButton
                  size="small"
                  onClick={() => setChangeQueueMode(true)}
                  disabled={ticketStatus !== "open"}
                  style={{ marginRight: 8 }}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18n.t("ticketCard.panel.edit")}>
                <IconButton
                  size="small"
                  onClick={() => setEditMode(true)}
                  disabled={ticketStatus !== "open"}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title={i18n.t("ticketCard.panel.save")}>
              <IconButton size="small" onClick={handleSaveChanges}>
                <SaveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
      </Grid>
      <Divider className={classes.divider} />

      {/* Exibir informações sobre as filas */}
      <Grid container spacing={1} style={{ marginBottom: 8 }}>
        <Grid item xs={12}>
          <Typography variant="body2">
            <strong>Fila do ticket: </strong>
            {currentQueue || i18n.t("ticketCard.panel.unknownQueue")}
          </Typography>
        </Grid>
        {isCardFromDifferentQueue && cardQueue && (
          <Grid item xs={12}>
            <Typography variant="body2" color="error">
              <strong>Fila do cartão: </strong>
              {cardQueue}
            </Typography>
          </Grid>
        )}
      </Grid>

      {isCardFromDifferentQueue && (
        <>
          <Typography variant="body2" color="error" gutterBottom>
            {i18n.t("ticketCard.panel.cardFromDifferentQueue") || "Atenção: Este cartão pertence a uma fila diferente da atual"}
          </Typography>
          {ticketStatus === "open" && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SwapHorizIcon />}
              fullWidth
              onClick={() => setChangeQueueMode(true)}
              className={classes.button}
              size="small"
              style={{ marginTop: 8, marginBottom: 8 }}
            >
              {i18n.t("ticketCard.panel.changeQueue") || "Mudar de Fila"}
            </Button>
          )}
          <Divider className={classes.divider} />
        </>
      )}

      {editMode ? (
        // Formulário de edição
        <>
          <TextField
            label={i18n.t("ticketCard.panel.title")}
            name="title"
            value={formData.title}
            onChange={handleChange}
            fullWidth
            margin="dense"
            variant="outlined"
            size="small"
            className={classes.formControl}
          />
          
          <TextField
            label={i18n.t("ticketCard.panel.description")}
            name="description"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={3}
            margin="dense"
            variant="outlined"
            className={classes.formControl}
          />
          
          <FormControl 
            variant="outlined" 
            fullWidth 
            className={classes.formControl}
            size="small"
          >
            <InputLabel>{i18n.t("ticketCard.panel.stage")}</InputLabel>
            <Select
              name="stageId"
              value={formData.stageId || ""}
              onChange={handleChange}
              label={i18n.t("ticketCard.panel.stage")}
            >
              {/* Mostrar apenas os estágios da fila atual */}
              {queueStages.map(stage => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl 
            variant="outlined" 
            fullWidth 
            className={classes.formControl}
            size="small"
          >
            <InputLabel>{i18n.t("ticketCard.panel.priority")}</InputLabel>
            <Select
              name="priority"
              value={formData.priority || "medium"}
              onChange={handleChange}
              label={i18n.t("ticketCard.panel.priority")}
            >
              <MenuItem value="low">
                {i18n.t("ticketCard.panel.priorityOptions.low")}
              </MenuItem>
              <MenuItem value="medium">
                {i18n.t("ticketCard.panel.priorityOptions.medium")}
              </MenuItem>
              <MenuItem value="high">
                {i18n.t("ticketCard.panel.priorityOptions.high")}
              </MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label={i18n.t("ticketCard.panel.tags")}
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            fullWidth
            margin="dense"
            variant="outlined"
            size="small"
            className={classes.formControl}
            placeholder="tag1, tag2, tag3"
          />
          
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
            <DatePicker
              disableToolbar
              variant="inline"
              inputVariant="outlined"
              format="dd/MM/yyyy"
              margin="normal"
              label={i18n.t("ticketCard.panel.dueDate")}
              value={formData.dueDate}
              onChange={handleDateChange}
              fullWidth
              className={classes.datePickerField}
              size="small"
              clearable="true"
              autoOk
            />
          </MuiPickersUtilsProvider>
        </>
      ) : (
        // Modo de visualização
        <>
          <Typography variant="subtitle1" gutterBottom>
            {card.title}
          </Typography>
          
          {card.description && (
            <Typography variant="body2" gutterBottom style={{ whiteSpace: 'pre-line' }}>
              {card.description}
            </Typography>
          )}
          
          {/* Botão para visualizar o cartão no Kanban */}
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={<ViewColumnIcon />}
            className={classes.button}
            size="small"
            onClick={() => {
              // Obter o ID da fila do cartão
              const queueIdToUse = card.stage && card.stage.queueId 
                ? card.stage.queueId 
                : (card.queueId || "");
              
              // Abrir a página do Kanban com os parâmetros cardId e queueId
              window.open(`/kanban?cardId=${card.id}&queueId=${queueIdToUse}`, '_blank');
            }}
          >
            {i18n.t("ticketCard.panel.viewInKanban") || "Ver no Kanban"}
          </Button>
          
          <Divider className={classes.divider} />
          
          <Grid container spacing={1}>
            {card.stage && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>{i18n.t("ticketCard.panel.stage")}: </strong>
                  {card.stage.name}
                </Typography>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography 
                variant="body2" 
                className={getPriorityClass(card.priority)}
              >
                <strong>{i18n.t("ticketCard.panel.priority")}: </strong>
                {getPriorityLabel(card.priority)}
              </Typography>
            </Grid>
            
            {card.tags && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>{i18n.t("ticketCard.panel.tags")}: </strong>
                  {card.tags}
                </Typography>
              </Grid>
            )}
            
            {card.dueDate && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>{i18n.t("ticketCard.panel.dueDate")}: </strong>
                  {format(new Date(card.dueDate), "dd/MM/yyyy")}
                </Typography>
              </Grid>
            )}
          </Grid>
          
          {nextStage && ticketStatus === "open" && !isCardFromDifferentQueue && (
            <>
              <Divider className={classes.divider} />
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowForwardIcon />}
                fullWidth
                onClick={() => handleMoveCard(nextStage.id)}
                className={classes.button}
                size="small"
              >
                {i18n.t("ticketCard.panel.moveToStage", { stage: nextStage.name })}
              </Button>
            </>
          )}
        </>
      )}

      {/* Mensagens vinculadas ao cartão */}
      {!editMode && (
        <>
          <Divider className={classes.divider} />
          <div 
            className={classes.messagesTitle}
            onClick={() => setMessagesExpanded(!messagesExpanded)}
          >
            <Typography variant="subtitle2">
              {i18n.t("kanban.fields.messages") || "Mensagens"}
            </Typography>
            <div className={classes.messageCount}>
              <Badge 
                badgeContent={cardMessages.length} 
                color="primary"
                style={{ marginRight: 8 }}
              >
                <MessageIcon fontSize="small" />
              </Badge>
              {messagesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
          </div>
          
          <Collapse in={messagesExpanded}>
            <div className={classes.messagesSection}>
              {loadingMessages ? (
                <div className={classes.progressContainer}>
                  <CircularProgress size={20} />
                </div>
              ) : cardMessages.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("kanban.noMessages") || "Nenhuma mensagem vinculada"}
                </Typography>
              ) : (
                <List dense>
                  {cardMessages.slice(0, 3).map((message) => (
                    <ListItem key={message.id} className={classes.messageItem}>
                      <ListItemAvatar>
                        <Avatar className={classes.avatar}>
                          {message.message.contact ? "C" : "A"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <div className={classes.messageHeader}>
                            <Typography variant="body2" component="span">
                              <strong>
                                {message.message.contact ? message.message.contact.name : "Atendente"}
                              </strong>
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {format(new Date(message.message.createdAt), "dd/MM/yyyy HH:mm")}
                            </Typography>
                          </div>
                        }
                        secondary={
                          <Typography 
                            variant="body2" 
                            className={classes.messageContent}
                            style={{ maxHeight: '60px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {message.message.body}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                  
                  {cardMessages.length > 3 && (
                    <Button
                      size="small"
                      color="primary"
                      fullWidth
                      onClick={() => {
                        // Abre o modal de detalhes para ver todas as mensagens
                        // (como está implementado em outro lugar)
                      }}
                    >
                      {i18n.t("kanban.viewAllMessages", { count: cardMessages.length - 3 }) || 
                        `Ver todas as ${cardMessages.length} mensagens`}
                    </Button>
                  )}
                </List>
              )}
            </div>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

export default TicketKanbanPanel;